import { Customer } from '../types';

export interface CustomerStats {
  revenueCents: number;
  projectCount: number;
}

/**
 * Umsatz gemergter Quellkunden dem Merge-Ziel zurechnen.
 *
 * Rechnungen bleiben beim Zusammenfuehren am alten Kunden (GoBD) — ohne
 * diese Faltung saehe der Zielkunde nach dem Merge aermer aus als die
 * Firma tatsaechlich ist. Ketten werden bis zum Ende verfolgt; ein Zyklus
 * (Datenfehler) bricht ab, statt zu haengen.
 */
/**
 * Endpunkt einer Merge-Kette. Folgt mergedIntoId bis zum letzten Kunden; ein
 * Zyklus (Datenfehler) bricht ab und liefert den zuletzt erreichten Kunden,
 * statt zu haengen.
 *
 * EINE Aufloesung fuer alle Aufrufer: Umsatzfaltung und Kundenakte muessen
 * dieselbe Kette sehen, sonst zeigt die Akte weniger Belege an, als der
 * Umsatz-KPI daneben zusammenzaehlt.
 */
const makeResolveFinal = (customers: Pick<Customer, 'id' | 'mergedIntoId'>[]) => {
  const target = new Map(customers.map((c) => [c.id, c.mergedIntoId]));
  return (id: string): string => {
    let cur = id;
    const seen = new Set<string>([cur]);
    while (true) {
      const next = target.get(cur);
      if (!next || seen.has(next)) return cur;
      seen.add(next);
      cur = next;
    }
  };
};

/**
 * Alle Kunden, deren Merge-Kette bei targetId endet — TRANSITIV: bei A -> B -> C
 * liefert C sowohl A als auch B.
 *
 * Die Kundenakte braucht das, um die Belege gemergter Vorgaenger mitzuzeigen.
 * Ein einstufiger Filter (mergedIntoId === targetId) uebersah bei mehrfach
 * zusammengefuehrten Kunden genau die Belege, die der Umsatz-KPI ueber
 * foldMergedRevenue laengst mitzaehlt.
 */
export const collectMergedSourceIds = (
  customers: Pick<Customer, 'id' | 'mergedIntoId'>[],
  targetId: string,
): string[] => {
  const resolveFinal = makeResolveFinal(customers);
  return customers
    .filter((c) => c.id !== targetId && !!c.mergedIntoId && resolveFinal(c.id) === targetId)
    .map((c) => c.id);
};

export const foldMergedRevenue = (
  revenueByCustomer: Map<string, number>,
  customers: Pick<Customer, 'id' | 'mergedIntoId'>[],
): Map<string, number> => {
  const resolveFinal = makeResolveFinal(customers);
  const out = new Map(revenueByCustomer);
  for (const [id, cents] of revenueByCustomer) {
    const final = resolveFinal(id);
    if (final !== id) out.set(final, (out.get(final) ?? 0) + cents);
  }
  return out;
};
